import { has } from 'lodash';
import compose from 'lodash/fp/compose';
import omit from 'lodash/omit';

import api from '../REST/api';
import { removeUnchangedValues } from '../utils/remove-unchanged-values';
import {
  replaceNestedWithId,
  replaceNestedWithIds,
} from '../utils/replace-nested-with-id';
import { salesChannelsEnum } from './constants';
import getOpportunityWithBrandIds from './utils/get-opportunity-with-brandids';

export function transformForRendering(opportunity) {
  const transformedOpportunity = opportunity;

  transformedOpportunity.brandCodes = opportunity.brands
    ? opportunity.brands.map(
        brand => brand.code || `${brand.name}:No brand code`,
      )
    : [];

  transformedOpportunity.merchantIds = opportunity.merchants
    ? opportunity.merchants.map(item => item.id)
    : [];

  transformedOpportunity.ownerId = transformedOpportunity.owner?.id;

  return compose([
    opp => replaceNestedWithIds(opp, 'brands'),
    opp => replaceNestedWithIds(opp, 'targetSuppliers'),
    opp => replaceNestedWithId(opp, 'account'),
    opp => replaceNestedWithId(opp, 'primaryQuote'),
    opp => replaceNestedWithId(opp, 'pc1'),
    opp => replaceNestedWithId(opp, 'pc2'),
    opp => replaceNestedWithId(opp, 'commercialPackage'),
  ])(transformedOpportunity);
}

export function getTransformedOpportunity(arg) {
  return api
    .getOpportunity(arg)
    .then(opportunity => transformForRendering(opportunity || {}));
}

function getFieldsToRemove(model) {
  const fieldsToRemove = [
    'account',
    'commercialPackage',
    'brands',
    'campaignId',
    'created',
    'createdBy',
    'id',
    'lastModified',
    'lastModifiedBy',
    'number',
    'owner',
    'probability',
    'primaryQuote',
    'expectedRevenue',
    'collaborators',
    'pool',
    'subPool',
    'merchants',
  ];
  if (model.salesChannel === salesChannelsEnum.SELF_SERVICE) {
    return fieldsToRemove.concat([
      'selfServiceMerchant',
      'selfServiceNCode',
      'selfServiceCampaignStatus',
      'selfServiceBudget',
      'selfServiceSpentBudget',
      'selfServiceFreeMedia',
    ]);
  }
  if (model.campaignId) {
    return fieldsToRemove.concat([
      'expectedRevenueMedia',
      'expectedRevenueCI',
      'expectedRevenueOther',
      'expectedRevenueCreative',
    ]);
  }
  return fieldsToRemove;
}

function removeInvalidAgreements(model) {
  const validAgreementOpportunities = model.agreementOpportunities.filter(
    aO => aO.budget && aO.agreement,
  );
  return {
    ...model,
    agreementOpportunities: validAgreementOpportunities,
  };
}

function replaceBrandCodesWithBrandIds(opp) {
  // The func needs to be called only when brandCodes are available.
  // If empty arr is passed then all brandCodes get linked.
  if (has(opp, 'brandCodes') && opp.brandCodes.length)
    return getOpportunityWithBrandIds(opp);
  /* eslint-disable no-param-reassign */
  delete opp.brandCodes;
  return opp;
}

function checkForMerchants(opp, merchants) {
  if (merchants.length && opp.salesChannel !== salesChannelsEnum.SELF_SERVICE)
    return true;
  return false;
}

export const transformForPatch = (model, oldModel, merchants = []) => {
  return compose([
    opp => replaceBrandCodesWithBrandIds(opp),
    opp => removeUnchangedValues(opp, oldModel),
    opp => omit(opp, getFieldsToRemove(opp)),
    opp => removeInvalidAgreements(opp),
    opp =>
      checkForMerchants(opp, merchants)
        ? { ...opp, merchantIds: opp.merchantIds }
        : opp,
  ])(model);
};
